import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CLONAL_SNP_THRESHOLD,
  CLOSELY_RELATED_SNP_THRESHOLD
} from './snpThresholds';
import { isNoReferenceSkipReason, noReferenceLabelForSkipReason } from './noReferenceComparisons';

export const formatValue = (value) => (
  (value == null || Number.isNaN(value)) ? '—' : String(Math.round(value))
);

export const SNP_BUCKETS = [
  {
    name: 'Clonal',
    range: `<= ${CLONAL_SNP_THRESHOLD} SNPs`,
    max: CLONAL_SNP_THRESHOLD + 1
  },
  {
    name: 'Closely related',
    range: `${CLONAL_SNP_THRESHOLD + 1} - ${CLOSELY_RELATED_SNP_THRESHOLD} SNPs`,
    max: CLOSELY_RELATED_SNP_THRESHOLD + 1
  },
  {
    name: 'Distantly related',
    range: `> ${CLOSELY_RELATED_SNP_THRESHOLD} SNPs`,
    max: Infinity
  }
].map((bucket) => ({
  ...bucket,
  label: `${bucket.name} (${bucket.range})`
}));

export const bucketIndexFor = ({ value, buckets }) => {
  for (let index = 0; index < buckets.length; index += 1) {
    if (value < buckets[index].max) {
      return index;
    }
  }
  return buckets.length - 1;
};

export const getBucketColors = (theme) => [
    theme.palette.primary.dark,
    theme.palette.primary.light,
    theme.palette.grey[400]
];

export const backgroundFor = ({ value, isDiagonal, skipReason, theme }) => {
  if (isDiagonal) {
    return getBucketColors(theme)[0];
  }

  if (value == null || Number.isNaN(value)) {
    if (isNoReferenceSkipReason(skipReason)) {
      return alpha(theme.palette.warning.main, 0.35);
    }
    return theme.palette.grey[100];
  }

  return getBucketColors(theme)[bucketIndexFor({ value, buckets: SNP_BUCKETS })];
};

function ClonalityHeatmapCell({
  rowSample,
  colSample,
  value,
  skipReason,
  isDiagonal,
  hideValueText,
  cellSize,
  sx
}) {
  const theme = useTheme();
  const backgroundColor = backgroundFor({
    value,
    isDiagonal,
    skipReason,
    theme
  });
  const methodName = 'SNPs';
  const isMissing = value == null || Number.isNaN(value);
  const missingReason = noReferenceLabelForSkipReason(skipReason) || 'no comparison available';

  const tooltipTitle = isDiagonal
    ? isMissing
      ? `${rowSample.name}: same sample`
      : `${rowSample.name}: ${formatValue(value)} (${methodName})`
    : isMissing
      ? `${rowSample.name} ↔ ${colSample.name}: ${missingReason}`
      : `${rowSample.name} ↔ ${colSample.name}: ${formatValue(value)} (${methodName})`;

  return (
    <Tooltip title={tooltipTitle}>
      <Box
        role="gridcell"
        tabIndex={0}
        sx={{
          width: cellSize,
          height: cellSize,
          minWidth: cellSize,
          maxWidth: cellSize,
          minHeight: cellSize,
          maxHeight: cellSize,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0,
          overflow: 'hidden',
          backgroundColor,
          color: theme.palette.getContrastText(backgroundColor),
          fontSize: 12,
          ...sx
        }}
      >
        {hideValueText
          ? ''
          : isMissing
            ? (isDiagonal ? '' : '—')
            : formatValue(value)}
      </Box>
    </Tooltip>
  );
}

ClonalityHeatmapCell.propTypes = {
  rowSample: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  colSample: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  value: PropTypes.number,
  skipReason: PropTypes.string,
  isDiagonal: PropTypes.bool.isRequired,
  hideValueText: PropTypes.bool,
  cellSize: PropTypes.number,
  sx: PropTypes.object
};

ClonalityHeatmapCell.defaultProps = {
  value: null,
  skipReason: null,
  hideValueText: false,
  cellSize: 36,
  sx: {}
};

export default ClonalityHeatmapCell;
